import React, { useEffect, useState } from 'react';
import { X, Sparkles, Users, Flame, TrendingUp, ArrowRight } from 'lucide-react';
import './SkoolCommunityPopup.css';

const STORAGE_KEY = 'ca_skool_popup_v1';
const SKOOL_URL = 'https://www.skool.com/the-collins-academy-2080';

/**
 * Flashy Skool-community popup.
 * Replaces the previous ExitIntentPopup. Fires on desktop exit-intent
 * or after 25s on mobile. Persists "shown" flag in sessionStorage.
 */
const SkoolCommunityPopup = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    let armed = false;
    let touchTimer;
    const fire = () => {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
      sessionStorage.setItem(STORAGE_KEY, '1');
      setOpen(true);
    };

    const armTimer = setTimeout(() => { armed = true; }, 6000);

    const onMouseOut = (e) => {
      if (!armed) return;
      if (e.clientY <= 0 && !e.relatedTarget) fire();
    };

    const isMobile = window.matchMedia('(max-width: 720px), (pointer: coarse)').matches;
    if (isMobile) {
      touchTimer = setTimeout(fire, 25000);
    } else {
      document.addEventListener('mouseout', onMouseOut);
    }

    return () => {
      clearTimeout(armTimer);
      clearTimeout(touchTimer);
      document.removeEventListener('mouseout', onMouseOut);
    };
  }, []);

  const close = () => setOpen(false);

  if (!open) return null;

  return (
    <div
      className="sk-pop"
      role="dialog"
      aria-modal="true"
      aria-label="Join The Collins Academy community"
      data-testid="skool-community-popup"
      onClick={close}
    >
      <div className="sk-pop-shell" onClick={(e) => e.stopPropagation()}>
        {/* Animated gradient border */}
        <span className="sk-pop-border" aria-hidden="true" />

        {/* Floating sparkle particles */}
        <span className="sk-pop-dot sk-pop-dot-1" aria-hidden="true" />
        <span className="sk-pop-dot sk-pop-dot-2" aria-hidden="true" />
        <span className="sk-pop-dot sk-pop-dot-3" aria-hidden="true" />
        <span className="sk-pop-dot sk-pop-dot-4" aria-hidden="true" />
        <span className="sk-pop-dot sk-pop-dot-5" aria-hidden="true" />

        <div className="sk-pop-panel">
          <button
            type="button"
            className="sk-pop-close"
            onClick={close}
            aria-label="Close"
            data-testid="skool-popup-close"
          >
            <X size={16} />
          </button>

          <span className="sk-pop-badge" data-testid="skool-popup-badge">
            <span className="sk-pop-live-dot" aria-hidden="true" />
            Live Community · Daily Drops
          </span>

          <h2 className="sk-pop-headline" data-testid="skool-popup-headline">
            Want <em>hands-on</em> daily help with
            <br />
            <span className="sk-pop-gradient">credit · funding · tradelines?</span>
          </h2>

          <p className="sk-pop-sub">
            Jump inside <strong>The Collins Academy</strong> — my private Skool community
            where I personally teach, drop plays, and answer questions in real time.
            New wins every day.
          </p>

          <ul className="sk-pop-perks" data-testid="skool-popup-perks">
            <li>
              <span className="sk-pop-perk-icon"><Users size={14} /></span>
              Private members-only community
            </li>
            <li>
              <span className="sk-pop-perk-icon"><Flame size={14} /></span>
              Daily credit, funding &amp; tradeline drops
            </li>
            <li>
              <span className="sk-pop-perk-icon"><TrendingUp size={14} /></span>
              Live Q&amp;A — ask me anything, get an answer
            </li>
            <li>
              <span className="sk-pop-perk-icon"><Sparkles size={14} /></span>
              Classroom modules, templates, and script packs
            </li>
          </ul>

          <a
            href={SKOOL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="sk-pop-cta"
            onClick={close}
            data-testid="skool-popup-cta"
          >
            <span className="sk-pop-cta-shimmer" aria-hidden="true" />
            <span className="sk-pop-cta-label">
              Join The Collins Academy on Skool
              <ArrowRight size={18} />
            </span>
          </a>

          <button
            type="button"
            className="sk-pop-decline"
            onClick={close}
            data-testid="skool-popup-decline"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkoolCommunityPopup;
